import { ApiError, getRegionalDepartments, putRegionalDepartments, type RegionalDepartmentDto } from "@synthesis/shared-services";
import type { SelectItem, ServerSearch } from "@synthesis/shared-ui";

import { createGroupServerSearch, type GroupSearchResult } from "../../../shared/hooks/use-group-search";

export type RegionDepartment = {
  id: string;
  name: string;
};

export type RegionDepartmentRow = {
  rowId: string;
  departmentId: string;
};

export const REGION_DEPARTMENTS_ASSIGN_SNACK_MESSAGES = {
  pending: "Выполняется назначение...",
  fulfilled: "Региональные подразделения назначены",
  rejected: "Не удалось назначить региональные подразделения. Попробуйте еще раз",
} as const;

export const REGION_DEPARTMENTS_UPDATE_SNACK_MESSAGES = {
  pending: "Выполняется обновление списка...",
  fulfilled: "Список причастных обновлён",
  rejected: "Не удалось обновить список. Попробуйте еще раз",
} as const;

export const REGION_DEPARTMENTS_DELETE_SNACK_MESSAGES = {
  pending: "Выполняется удаление...",
  fulfilled: "Подразделение удалено",
  rejected: "Не удалось удалить подразделение. Попробуйте еще раз",
} as const;

export const REGION_DEPARTMENTS_DUPLICATE_SNACK_MESSAGE = "Отправка невозможна: есть повторяющиеся подразделения.";

export const regionalDepartmentsQueryKey = (requestId: string) => ["regional-departments", requestId] as const;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const createRowId = (): string => crypto.randomUUID();

export const createEmptyDepartmentRow = (): RegionDepartmentRow => ({
  rowId: createRowId(),
  departmentId: "",
});

const readDepartmentName = (record: Record<string, unknown>): string | null => {
  const name = record.name ?? record.title ?? record.label ?? record.displayName ?? record.departmentName;

  if (typeof name === "string" && name.trim()) {
    return name.trim();
  }

  return null;
};

const readDepartmentId = (record: Record<string, unknown>): string | null => {
  const id = record.id ?? record.departmentId ?? record.groupId ?? record.internalDepartmentId;

  if (typeof id === "string" && id.trim()) {
    return id.trim();
  }

  return null;
};

const mapUnknownToRegionDepartment = (value: unknown): RegionDepartment | null => {
  if (!isRecord(value)) {
    return null;
  }

  const id = readDepartmentId(value);
  const name = readDepartmentName(value);

  if (!id || !name) {
    return null;
  }

  return { id, name };
};

export const parseRegionDepartmentsFromApplication = (application: Record<string, unknown>): RegionDepartment[] => {
  const candidateKeys = [
    "regionDepartments",
    "relatedDepartments",
    "technicalDepartments",
    "regionalDepartments",
    "assignedRegionDepartments",
  ] as const;

  for (const key of candidateKeys) {
    const value = application[key];

    if (!Array.isArray(value)) {
      continue;
    }

    const departments = value.map(mapUnknownToRegionDepartment).filter((department): department is RegionDepartment => department !== null);

    if (departments.length > 0) {
      return departments;
    }
  }

  return [];
};

export const getInitialDepartmentRows = (departments: RegionDepartment[]): RegionDepartmentRow[] => {
  if (departments.length === 0) {
    return [createEmptyDepartmentRow()];
  }

  return departments.map((department) => ({
    rowId: createRowId(),
    departmentId: department.id,
  }));
};

export const getRegionDepartmentLabel = (departmentId: string, departments: RegionDepartment[]): string | undefined => {
  const department = departments.find((item) => item.id === departmentId);

  return department?.name;
};

export const getSelectedDepartmentIds = (rows: RegionDepartmentRow[]): string[] =>
  rows.map((row) => row.departmentId).filter((departmentId) => departmentId.length > 0);

const DUPLICATE_REGION_DEPARTMENT_ERROR_MARKERS = [
  "uq_regional_department_request_department",
  "duplicate key value violates unique constraint",
] as const;

/**
 * Распознаёт ошибку БД о дубликате регионального подразделения по ответу сервера:
 * 500 INTERNAL_ERROR с detail вида `duplicate key value violates unique constraint
 * "uq_regional_department_request_department"`.
 */
export const isDuplicateRegionalDepartmentError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) {
    return false;
  }

  const errorText = `${error.response?.detail ?? ""} ${error.response?.description ?? ""}`;

  return DUPLICATE_REGION_DEPARTMENT_ERROR_MARKERS.some((marker) => errorText.includes(marker));
};

export const getSelectedDepartments = (rows: RegionDepartmentRow[], nameMap: Record<string, string>): RegionDepartment[] => {
  const result: RegionDepartment[] = [];

  for (const row of rows) {
    const name = nameMap[row.departmentId];

    if (row.departmentId.length > 0 && name) {
      result.push({ id: row.departmentId, name });
    }
  }

  return result;
};

export const getRegionDepartmentSnackMessages = (hasExistingDepartments: boolean) =>
  hasExistingDepartments ? REGION_DEPARTMENTS_UPDATE_SNACK_MESSAGES : REGION_DEPARTMENTS_ASSIGN_SNACK_MESSAGES;

const toDepartmentSelectItem = (group: GroupSearchResult): SelectItem => ({
  value: group.id,
  label: group.name,
  data: group,
});

export const createRegionDepartmentServerSearch = (): ServerSearch => createGroupServerSearch(undefined, toDepartmentSelectItem);

export const fetchRegionalDepartments = (requestId: string, signal?: AbortSignal): Promise<RegionalDepartmentDto[]> =>
  getRegionalDepartments(requestId, signal);

export const saveRegionDepartments = async (requestId: string, departments: RegionDepartment[]): Promise<RegionalDepartmentDto[]> =>
  putRegionalDepartments(requestId, { departments });

export const deleteRegionDepartment = async (
  requestId: string,
  departments: RegionDepartment[],
  departmentId: string,
): Promise<RegionalDepartmentDto[]> =>
  putRegionalDepartments(requestId, { departments: departments.filter((department) => department.id !== departmentId) });
